import { FETCH_RISK_HUB_MATRIX } from '../queries';
import { useQuery } from '@apollo/client';

import { useLocation, useSearchParams } from 'react-router-dom';
import { FETCH_LOCATION_RISK_HUB_MATRIX } from '../queries/RiskHub';
import TableV3 from '../../../components/generic/Table/TableV3';
import { ColumnsType, ColumnType } from 'antd/es/table';
import { tableSortingMethods } from '../../../utils';
import FinancialData from '../../../components/common/FinancialData';
import QueryErrorBoundary from '../../../components/error-handling';
import { ExportColumn } from '../../../interfaces/ExportColumn';
import { camelCase, startCase } from 'lodash';
import { Division, DivisionView } from '../../../interfaces/RiskHub';
import { useUserContext } from '../../../context/PermissionsContext';
import { getFilteredTags } from '../../../utils/getFilteredTags';

export function DependencyMatrixCard() {
  let [searchParams] = useSearchParams();
  const location = useLocation();
  const divisionUuid = searchParams.get('division');
  const locationUuid = searchParams.get('location');

  const pathname = location.pathname;
  const IS_LOCATION_RISK_HUB = pathname === '/location-risk-hub';

  const query = IS_LOCATION_RISK_HUB
    ? FETCH_LOCATION_RISK_HUB_MATRIX
    : FETCH_RISK_HUB_MATRIX;

  const { canViewFinancials, isLocation, selectedTags, isDivision } =
    useUserContext();

  const { data, loading, error } = useQuery(query, {
    variables: {
      filter: IS_LOCATION_RISK_HUB ? { locationUuid } : { divisionUuid },
    },
  });

  const riskHubData = IS_LOCATION_RISK_HUB
    ? data?.processAreaMatrix
    : data?.divisionView;

  const filteredData = !IS_LOCATION_RISK_HUB
    ? getFilteredTags({
        data: riskHubData,
        selectedTags,
        condition: isLocation,
        lookUpKey: 'location.uuid',
      })
    : riskHubData;

  const getColumns = (data: DivisionView[]): ColumnsType<any[]> => {
    if (!data || data.length === 0) {
      return [];
    }

    const firstDivision = data[0]?.divisions ?? [];

    const filteredDivisionColumns = isDivision
      ? firstDivision.filter((column) => selectedTags?.includes(column.uuid))
      : firstDivision;

    const divisionColumns: ColumnType<DivisionView>[] =
      filteredDivisionColumns?.map((division, index) => ({
        title: startCase(division.name.toLowerCase()),
        key: division.name,
        uuid: division.uuid,
        width: 100,
        align: 'right',
        sorter: (a, b) =>
          tableSortingMethods.number(
            a.divisions[index]?.percentage,
            b.divisions[index]?.percentage,
          ),
        render: (_, { divisions }) => {
          const divis = divisions.find((div) => div.uuid === division.uuid);
          return (
            <span className="custom-table--column">
              {divis?.percentage?.toFixed() ?? 0}%
            </span>
          );
        },
      }));

    const baseColumns: ColumnsType<any> = [
      {
        key: 'location',
        title: IS_LOCATION_RISK_HUB ? 'Process Area' : 'Location',
        width: 100,
        align: 'left',
        sorter: (a, b) =>
          IS_LOCATION_RISK_HUB
            ? tableSortingMethods.string(a.processArea, b.processArea)
            : tableSortingMethods.string(
                a.location.locationName,
                b.location.locationName,
              ),
        render: (_, record) => (
          <div className="flex flex-col">
            <span className="custom-table--column custom-table--column-primary">
              {IS_LOCATION_RISK_HUB
                ? record.processArea
                : record.location.locationName}
            </span>
            {!IS_LOCATION_RISK_HUB && (
              <span className="!font-medium text-sm !italic !text-secondary-300 pl-6">
                {record.location.locationType.split(/(?=[A-Z])/).join(' ')}
              </span>
            )}
          </div>
        ),
      },
      ...divisionColumns,
      {
        key: 'totalRevenue',
        title: 'Annual Revenue Supported',
        align: 'right',
        width: 100,
        sorter: canViewFinancials
          ? (a, b) => tableSortingMethods.number(a.totalRevenue, b.totalRevenue)
          : false,
        render: (_, { totalRevenue }) => (
          <span className="custom-table--column custom-table--column-revenue">
            <FinancialData text={totalRevenue} />
          </span>
        ),
      },
      {
        key: 'totalPercentage',
        title: IS_LOCATION_RISK_HUB
          ? '% Of Location Supported'
          : '% Of Company Supported',
        width: 100,
        align: 'right',
        sorter: (a, b) =>
          tableSortingMethods.number(a.totalPercentage, b.totalPercentage),
        render: (_, { totalPercentage }) => (
          <span className="custom-table--column">
            {totalPercentage.toFixed()}%
          </span>
        ),
      },
    ];

    return baseColumns;
  };

  const baseColumns: ExportColumn[] = [
    {
      key: IS_LOCATION_RISK_HUB ? 'processArea' : 'location.locationName',
      header: IS_LOCATION_RISK_HUB ? 'Process Area' : 'Location',
      type: 'text',
      width: 40,
    },
  ];

  if (!IS_LOCATION_RISK_HUB) {
    baseColumns.push({
      key: 'location.locationType',
      header: 'Type',
      type: 'text',
      width: 30,
    });
  }

  const percentageColumns: ExportColumn[] = [];

  if (riskHubData && riskHubData.length > 0) {
    riskHubData[0].divisions?.forEach((division: Division, index: number) => {
      percentageColumns.push({
        key: camelCase(division.name),
        header: division.name,
        type: 'percentage',
        nestedKey: `divisions.${index}.percentage`,
        width: 35,
      });
    });
  }

  const exportExcelColumns: ExportColumn[] = [
    ...baseColumns,
    ...percentageColumns,
    {
      key: 'totalRevenue',
      header: 'Annual Revenue Supported',
      type: 'money',
      width: 35,
    },
    {
      key: 'totalPercentage',
      header: IS_LOCATION_RISK_HUB
        ? '% of Location Supported'
        : 'Total Percentage',
      type: 'percentage',
    },
  ];

  return (
    <QueryErrorBoundary error={error}>
      <TableV3
        title="Dependency Matrix"
        columns={getColumns(riskHubData) as ColumnsType<DivisionView>}
        data={filteredData}
        loading={loading}
        customStyles={'custom-column-PA--heading custom-table--risk-hub'}
        exportExcelColumns={exportExcelColumns}
      />
    </QueryErrorBoundary>
  );
}
